/**
 * Groups detected entity suggestions for the results panel.
 *
 * The heavy lifting (case-insensitive + fuzzy merging of OCR variants of the
 * same value) happens on the backend, which stamps every occurrence with a
 * shared `groupKey` and a canonical `groupLabel`. Here we simply group by that
 * key so one logical entity shows as a single row — while keeping every
 * occurrence (each is still redacted at its own bounding box).
 *
 * When `groupKey` is absent (e.g. optimistic manual additions, or data from
 * before the backend grouping shipped) we fall back to grouping by the
 * case/whitespace-normalized text, so behavior degrades gracefully.
 */
import type { EntitySuggestion, EntityType } from '@/types';

export interface EntityGroup {
  key: string;
  entityType: EntityType;
  /** Canonical display spelling for the group. */
  text: string;
  suggestions: EntitySuggestion[];
}

/**
 * Trim, drop numeric parentheticals (legal "word (N)" restatements like
 * "six (6) months"), collapse whitespace, and lowercase for comparison.
 * Mirrors the backend `normalize_entity_text`.
 */
export function normalizeEntityText(text: string): string {
  return text
    .trim()
    .replace(/\(\s*\d+\s*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** The group key for a single suggestion (backend key, or local fallback). */
function groupKeyFor(s: EntitySuggestion): string {
  return s.groupKey ?? `${s.entityType}::${normalizeEntityText(s.text)}`;
}

/** Pick the canonical display text for a group. */
function canonicalText(suggestions: EntitySuggestion[]): string {
  // Prefer the backend-provided label; it's consistent across occurrences.
  const labeled = suggestions.find((s) => s.groupLabel);
  if (labeled?.groupLabel) return labeled.groupLabel;

  // Fallback: the most common raw spelling, tie-broken to the longer one.
  const freq = new Map<string, number>();
  for (const s of suggestions) freq.set(s.text, (freq.get(s.text) ?? 0) + 1);
  let best = suggestions[0]?.text ?? '';
  let bestCount = -1;
  for (const [text, count] of freq) {
    if (count > bestCount || (count === bestCount && text.length > best.length)) {
      best = text;
      bestCount = count;
    }
  }
  return best;
}

/** Group suggestions for display by their (backend-computed) group key. */
export function groupSuggestions(suggestions: EntitySuggestion[]): EntityGroup[] {
  const groupMap = new Map<string, EntitySuggestion[]>();
  for (const s of suggestions) {
    const key = groupKeyFor(s);
    const arr = groupMap.get(key);
    if (arr) arr.push(s);
    else groupMap.set(key, [s]);
  }

  const groups: EntityGroup[] = [];
  for (const [key, groupSuggestionsList] of groupMap) {
    const sorted = groupSuggestionsList.slice().sort((a, b) => a.page - b.page);
    groups.push({
      key,
      entityType: sorted[0].entityType,
      text: canonicalText(sorted),
      suggestions: sorted,
    });
  }
  return groups;
}

/** Map each suggestion's stable entityId to the key of the group it belongs to. */
export function buildIdToGroupKey(groups: EntityGroup[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const group of groups) {
    for (const s of group.suggestions) map.set(s.entityId, group.key);
  }
  return map;
}

/**
 * The suggestions with these entities retyped (optimistic update for a type
 * change, FB-1012): the new type and the matching display group. Group keys are
 * `TYPE::value` (server/services/redaction/entity_grouping.py), and the server
 * moves a retyped row to the new type's group the same way.
 */
export function retypeSuggestions(
  suggestions: EntitySuggestion[],
  entityIds: string[],
  entityType: EntityType,
): EntitySuggestion[] {
  const ids = new Set(entityIds);
  return suggestions.map((s) => {
    if (!ids.has(s.entityId)) return s;
    const groupKey = s.groupKey?.includes('::') ? `${entityType}::${s.groupKey.split('::').slice(1).join('::')}` : s.groupKey;
    return { ...s, entityType, groupKey };
  });
}

// What a "New category…" draft gets from a card (FB-1011), within the server's
// limits: its distinct spellings (10, 200 chars each) and rationales (5).
export function cardValues(group: EntityGroup): string[] {
  return [...new Set(group.suggestions.map((s) => s.text.trim().slice(0, 200)).filter(Boolean))].slice(0, 10);
}

export function cardContext(group: EntityGroup): string[] {
  return [...new Set(group.suggestions.map((s) => s.rationale?.trim()).filter((r): r is string => Boolean(r)))].slice(0, 5);
}
